import { createInstance } from "i18next";
import { initReactI18next } from "react-i18next";
import {
  detectInitialLocale,
  persistLocaleSelection,
  resolveSupportedLocale,
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
} from "./lib/locale-preferences";
import { APP_NAMESPACES } from "./i18n/namespaces";
import enIdentityUrl from "/locales/en/identity.json?url";
import enLayoutUrl from "/locales/en/layout.json?url";
import enNotificationsUrl from "/locales/en/notifications.json?url";
import esIdentityUrl from "/locales/es/identity.json?url";
import esLayoutUrl from "/locales/es/layout.json?url";
import esNotificationsUrl from "/locales/es/notifications.json?url";
import { formatI18nDateValue } from "./i18n/date-format";

const initialLocale = detectInitialLocale();

const syncDocumentLocale = (locale: string) => {
  if (typeof document === "undefined") {
    return;
  }

  const supportedLocale = resolveSupportedLocale(locale) ?? initialLocale;
  document.documentElement.lang = supportedLocale;
  document.documentElement.setAttribute("data-locale", supportedLocale);
};

export const getCurrentHostLocale = () =>
  resolveSupportedLocale(i18n.resolvedLanguage ?? i18n.language) ??
  initialLocale;

export const changeShellLanguage = async (locale: string) => {
  const supportedLocale = resolveSupportedLocale(locale);

  if (!supportedLocale) {
    throw new Error(
      `[i18n] Unsupported locale "${locale}" requested in shell.`,
    );
  }

  if (getCurrentHostLocale() === supportedLocale) {
    syncDocumentLocale(supportedLocale);
    persistLocaleSelection(supportedLocale);
    return supportedLocale;
  }

  await i18n.changeLanguage(supportedLocale);
  syncDocumentLocale(supportedLocale);
  persistLocaleSelection(supportedLocale);
  return supportedLocale;
};

const i18n = createInstance();

const loadLocaleResource = async (resourceUrl: string) => {
  const response = await fetch(resourceUrl);

  if (!response.ok) {
    throw new Error(
      `[i18n] Failed to load locale resource "${resourceUrl}" (${response.status})`,
    );
  }

  return response.json();
};

const formatI18nValue = (value: unknown, format?: string, lng?: string) => {
  if (!format || format !== "date") {
    return String(value ?? "");
  }

  const locale = lng ?? i18n.language ?? initialLocale;
  return formatI18nDateValue(value, locale);
};

if (!i18n.isInitialized) {
  void i18n
    .use(initReactI18next)
    .init({
      lng: initialLocale,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: SUPPORTED_LOCALES,
      defaultNS: "identity",
      ns: APP_NAMESPACES,
      interpolation: {
        escapeValue: false,
        format: formatI18nValue,
      },
      resources: {
        en: {
          identity: {},
          layout: {},
          notifications: {},
        },
        es: {
          identity: {},
          layout: {},
          notifications: {},
        },
      },
      react: {
        useSuspense: false,
      },
    })
    .then(() => {
      syncDocumentLocale(getCurrentHostLocale());
    })
    .catch((error) => {
      console.error("[i18n] Failed to initialize shell i18n", error);
    });
}

void Promise.all([
  loadLocaleResource(enIdentityUrl),
  loadLocaleResource(enLayoutUrl),
  loadLocaleResource(enNotificationsUrl),
  loadLocaleResource(esIdentityUrl),
  loadLocaleResource(esLayoutUrl),
  loadLocaleResource(esNotificationsUrl),
])
  .then(
    ([
      enIdentity,
      enLayout,
      enNotifications,
      esIdentity,
      esLayout,
      esNotifications,
    ]) => {
      if (typeof i18n.addResourceBundle !== "function") {
        throw new Error(
          "[i18n] addResourceBundle is not available on the shell i18n instance",
        );
      }

      i18n.addResourceBundle("en", "identity", enIdentity, true, true);
      i18n.addResourceBundle("en", "layout", enLayout, true, true);
      i18n.addResourceBundle(
        "en",
        "notifications",
        enNotifications,
        true,
        true,
      );
      i18n.addResourceBundle("es", "identity", esIdentity, true, true);
      i18n.addResourceBundle("es", "layout", esLayout, true, true);
      i18n.addResourceBundle(
        "es",
        "notifications",
        esNotifications,
        true,
        true,
      );
      syncDocumentLocale(getCurrentHostLocale());
    },
  )
  .catch((error) => {
    console.error("[i18n] Failed to hydrate locale resources", error);
  });

if (typeof window !== "undefined" && typeof i18n.on === "function") {
  i18n.on("languageChanged", (nextLocale) => {
    persistLocaleSelection(nextLocale);
    syncDocumentLocale(nextLocale);
  });
}

export default i18n;
